import type { ActionFunction } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type {
    UpdateMemberContactDetailsMutation,
    UpdateMemberContactDetailsMutationVariables,
} from 'generated/graphql';
import { UpdateMemberContactDetailsDocument } from 'generated/documents/UpdateMemberContactDetailsDocument';

export const MEMBER_UPDATE_CONTACT_DETAILS_API_ROUTE =
    '/remix-api/member/update-contact-details';

/**
 * Writes the contact details a member edited in a gate form back to their
 * profile, so the correction they made once is the one every later form
 * starts from.
 *
 * Only the fields present in the payload are sent on — an omitted field must
 * not be read as an instruction to clear it. Email is deliberately not
 * accepted: it is the account's identity and changing it is a different flow
 * with its own verification.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const { client } = context;
    const form = await request.formData();

    const variables: UpdateMemberContactDetailsMutationVariables = {};
    for (const field of ['FirstName', 'Surname', 'Phone', 'Suburb'] as const) {
        const value = form.get(field);
        if (value !== null) variables[field] = value.toString();
    }

    if (!Object.keys(variables).length) {
        return routerData({ error: 'Nothing to update' }, { status: 400 });
    }

    const result = await client.mutate<
        UpdateMemberContactDetailsMutation,
        UpdateMemberContactDetailsMutationVariables
    >({
        mutation: UpdateMemberContactDetailsDocument,
        variables,
    });

    return { success: !!result.data?.updateMe?.ID };
};
